import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = API_URL ? `${API_URL}/api` : '/api';

// Create axios instance with auth header
const createApiClient = () => {
  const client = axios.create({
    baseURL: API_BASE,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('episo_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  });

  return client;
};

const api = createApiClient();

// ============== TMDB API ==============

export const getTrending = async (mediaType = 'all', timeWindow = 'week') => {
  const response = await api.get(`/tmdb/trending`, {
    params: { media_type: mediaType, time_window: timeWindow }
  });
  return response.data;
};

export const getTrendingExtended = async (mediaType = 'movie', page = 1) => {
  const response = await api.get(`/tmdb/trending/${mediaType}/extended`, {
    params: { page }
  });
  return response.data;
};

export const searchMedia = async (query, mediaType = 'multi', page = 1) => {
  const response = await api.get(`/tmdb/search`, {
    params: { query, media_type: mediaType, page }
  });
  return response.data;
};

export const getMovieDetails = async (movieId) => {
  const response = await api.get(`/tmdb/movie/${movieId}`);
  return response.data;
};

export const getTvDetails = async (tvId) => {
  const response = await api.get(`/tmdb/tv/${tvId}`);
  return response.data;
};

export const getSeasonDetails = async (tvId, seasonNumber) => {
  const response = await api.get(`/tmdb/tv/${tvId}/season/${seasonNumber}`);
  return response.data;
};

export const getPersonDetails = async (personId) => {
  const response = await api.get(`/tmdb/person/${personId}`);
  return response.data;
};

export const getSimilar = async (mediaType, id) => {
  const response = await api.get(`/tmdb/similar/${mediaType}/${id}`);
  return response.data;
};

export const getUpcomingMovies = async (page = 1) => {
  const response = await api.get(`/tmdb/upcoming/movies`, { params: { page } });
  return response.data;
};

export const getUpcomingTv = async () => {
  const response = await api.get(`/tmdb/upcoming/tv`);
  return response.data;
};

export const getRecommendations = async (mediaType, id) => {
  const response = await api.get(`/tmdb/recommendations/${mediaType}/${id}`);
  return response.data;
};

// ============== USER LIBRARY ==============

export const getUserMovies = async (status = null) => {
  const params = status ? { status } : {};
  const response = await api.get(`/movies`, { params });
  return response.data;
};

export const addMovie = async (movie) => {
  const response = await api.post(`/movies`, movie);
  return response.data;
};

export const updateMovie = async (movieId, updates) => {
  const response = await api.put(`/movies/${movieId}`, updates);
  return response.data;
};

export const deleteMovie = async (movieId) => {
  const response = await api.delete(`/movies/${movieId}`);
  return response.data;
};

// ============== SERIES ==============

export const getUserSeries = async (status = null) => {
  const params = status ? { status } : {};
  const response = await api.get(`/series`, { params });
  return response.data;
};

export const addSeries = async (series) => {
  const response = await api.post(`/series`, series);
  return response.data;
};

export const updateSeries = async (seriesId, updates) => {
  const response = await api.put(`/series/${seriesId}`, updates);
  return response.data;
};

export const markEpisodeWatched = async (seriesId, season, episode, tmdbId) => {
  const response = await api.post(`/series/${seriesId}/watch-episode`, {
    season,
    episode,
    tmdb_id: tmdbId
  });
  return response.data;
};

export const markBulkEpisodesWatched = async (seriesId, episodes) => {
  const response = await api.post(`/series/${seriesId}/watch-bulk`, {
    episodes
  });
  return response.data;
};

export const unmarkEpisodeWatched = async (seriesId, season, episode, tmdbId) => {
  const response = await api.post(`/series/${seriesId}/unwatch-episode`, {
    season,
    episode,
    tmdb_id: tmdbId
  });
  return response.data;
};

export const deleteSeries = async (seriesId) => {
  const response = await api.delete(`/series/${seriesId}`);
  return response.data;
};

export const getContinueWatching = async () => {
  const response = await api.get(`/series/continue-watching`);
  return response.data;
};

// ============== STATS ==============

export const getUserStats = async () => {
  const response = await api.get(`/stats`);
  return response.data;
};

export const getPersonalizedRecommendations = async () => {
  const response = await api.get(`/recommendations/personalized`);
  return response.data;
};

// ============== TRAKT ==============

export const getTraktAuthUrl = async (redirectUri) => {
  const response = await api.get(`/trakt/auth-url`, {
    params: { redirect_uri: redirectUri }
  });
  return response.data;
};

export const traktCallback = async (code, redirectUri) => {
  const response = await api.post(`/trakt/callback`, {
    code,
    redirect_uri: redirectUri
  });
  return response.data;
};

export const syncWithTrakt = async () => {
  const response = await api.post(`/trakt/sync`);
  return response.data;
};

// ============== HELPERS ==============

export const getImageUrl = (path, size = 'w500') => {
  if (!path) return null;
  return `https://image.tmdb.org/t/p/${size}${path}`;
};

export const getWatchProviderLogo = (path) => {
  if (!path) return null;
  return `https://image.tmdb.org/t/p/original${path}`;
};

// Mapping of streaming provider IDs to their homepage URLs
// Source: TMDB watch provider IDs for NL region
export const STREAMING_PROVIDER_URLS = {
  8: 'https://www.netflix.com',        // Netflix
  9: 'https://www.amazon.nl/prime-video', // Amazon Prime Video
  337: 'https://www.disneyplus.com',   // Disney+
  350: 'https://www.apple.com/nl/apple-tv-plus/', // Apple TV+
  283: 'https://www.crunchyroll.com',  // Crunchyroll
  119: 'https://www.amazon.nl/prime-video', // Amazon Prime Video
  1899: 'https://www.max.com',         // Max (HBO Max)
  15: 'https://www.hulu.com',          // Hulu
  531: 'https://www.paramountplus.com', // Paramount+
  613: 'https://www.freevee.com',      // Freevee
  175: 'https://www.tv.nu',            // TV.nu
  423: 'https://www.videoland.com',    // Videoland
  563: 'https://www.viaplay.nl',       // Viaplay
  445: 'https://www.nlziet.nl',        // NLZiet
  444: 'https://www.skyshowtime.com',  // SkyShowtime
};

export const getProviderUrl = (providerId, fallbackLink) => {
  return STREAMING_PROVIDER_URLS[providerId] || fallbackLink;
};

// Fetch TVDB logo for a series
export const getTvdbLogo = async (seriesName) => {
  try {
    const response = await api.get(`/tvdb/logo`, { params: { name: seriesName } });
    return response.data.logo_url;
  } catch (error) {
    console.log('Could not fetch TVDB logo');
    return null;
  }
};

export default api;
